import { useRouter } from "next/router";
import { useMemo } from "react";

import AppAuthApi from "@/apis/connect/auth-api";
import AppColormeApi from "@/apis/connect/colorme-api";
import { MYPAGE_COLORME_LINKED_PATH } from "@/global-state/app-path";
import { idTokenAtom, loadingAtom } from "@/global-state/jotai-atoms";
import { beginColormeOAuth } from "@/utils/colorme-oauth";

import { useAtom } from "jotai";
import { useSnackbar } from "notistack";


// マイページ「外部連携設定（カラーミー）」用フック。
// loading(グローバル) + snackbar を面倒みつつ、連携状態取得 / 連携開始 / 連携解除 / コールバック連携を提供する。
export const useColormeApi = () => {
  const router = useRouter();
  const [, setLoading] = useAtom(loadingAtom);
  const [idToken] = useAtom(idTokenAtom);
  const { enqueueSnackbar } = useSnackbar();
  const colormeApi = useMemo(() => new AppColormeApi(idToken), [idToken]);

  const getColormeStatus = async () => {
    setLoading(true);
    try {
      return await colormeApi.getStatus();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message || "連携の状態を読み込めませんでした", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const unlinkColorme = async (shopId: string) => {
    setLoading(true);
    try {
      const res = await colormeApi.unlink(shopId);
      enqueueSnackbar("カラーミーショップの連携を解除しました", { variant: "success" });
      return res;
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message || "連携を解除できませんでした", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // 連携開始: link モードのフラグを立てて認可URLへ遷移する（認可URLはログインと共通で再利用）。
  const startColormeLink = async () => {
    try {
      const { authorize_url } = await new AppAuthApi().colormeAuthorizeUrl();
      beginColormeOAuth(authorize_url, "link");
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || "カラーミーショップの画面を開けませんでした", {
        variant: "error",
      });
    }
  };

  // コールバック（link モード）: ログイン中ユーザーとして code/state を link に渡し、マイページへ戻す。
  const linkColormeFromCallback = async (code: string, state: string) => {
    setLoading(true);
    try {
      await colormeApi.link({ code, state });
      enqueueSnackbar("カラーミーショップと連携しました", { variant: "success" });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || "カラーミーショップと連携できませんでした", { variant: "error" });
    } finally {
      setLoading(false);
      await router.push(MYPAGE_COLORME_LINKED_PATH);
    }
  };

  // カラーミーショップ限定プランの利用権を取得する。
  // silent=true（画面表示時の自動取得）はグローバルloadingもエラー通知も出さない。
  const getColormeEntitlement = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      return await colormeApi.getEntitlement();
    } catch (e: any) {
      if (!silent) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message || "ご契約の内容を読み込めませんでした", {
          variant: "error",
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // 引換券でショップを紐付ける（アカウント作成/ログインの直後に自動で呼ぶ）。
  // カラーミーショップの認証は済んでいるので、店主に同じ操作をさせない。
  const linkColormeByTicket = async (linkTicket: string) => {
    setLoading(true);
    try {
      const res = await colormeApi.linkByTicket({ link_ticket: linkTicket });
      enqueueSnackbar("カラーミーショップと連携しました", { variant: "success" });
      return res;
    } catch (e: any) {
      // 引換券は30分で切れる。切れた（400）ときは呼び出し側が認証をやり直すので、
      // 店主を不安にさせるエラーは出さない
      if (e?.response?.status !== 400) {
        enqueueSnackbar(e?.response?.data?.detail || "カラーミーショップと連携できませんでした", {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // インストール成功画面のショップ情報を取得する（未ログインで開かれるため認証不要）。
  // 失敗しても画面は出したいので、エラー通知は出さずに null を返す。
  const getColormeInstalledShop = async (shopId: string) => {
    try {
      return await colormeApi.getInstalledShop(shopId);
    } catch (e) {
      return null;
    }
  };

  // カラーミーショップ限定プランを適用するInstagramアカウントを設定・変更する。
  const updateColormeEntitlement = async (shopId: string, instagramAccountId: string) => {
    setLoading(true);
    try {
      const res = await colormeApi.updateEntitlement({
        shop_id: shopId,
        instagram_account_id: instagramAccountId,
      });
      enqueueSnackbar("カラーミーショップ限定プランを使うアカウントを設定しました", { variant: "success" });
      return res;
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message || "プランを使うアカウントを設定できませんでした", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    getColormeStatus,
    unlinkColorme,
    startColormeLink,
    linkColormeFromCallback,
    getColormeEntitlement,
    getColormeInstalledShop,
    linkColormeByTicket,
    updateColormeEntitlement,
  };
};
